// Growth stage logic and order calculations

export const GRAIN_TYPES = [
  { value: "barley", label: "Barley", chip: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  { value: "wheat",  label: "Wheat",  chip: "bg-yellow-100 text-yellow-800 border-yellow-200", dot: "bg-yellow-500" },
  { value: "corn",   label: "Corn",   chip: "bg-orange-100 text-orange-800 border-orange-200", dot: "bg-orange-500" },
];

export function grainTypeMeta(value) {
  return GRAIN_TYPES.find((g) => g.value === value) || GRAIN_TYPES[0];
}

// --- Phase model (hour-based, NOT day-based) -------------------------------
// 0–12h    : SOAK    (no water, no light)
// 12–60h   : DARK    (lights off, water 1 min every 6 h)
// 60–168h  : GROWTH  (16h light ON / 8h light OFF cycle, water 1 min every 4 h)
// 168h+    : READY   (harvest window / overdue)
export const PHASES = {
  soak: {
    key: "soak",
    label: "Soak Phase",
    short: "Soak",
    range: "0–12h",
    color: "amber",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    fill: "bg-amber-400",
  },
  dark: {
    key: "dark",
    label: "Dark Phase",
    short: "Dark",
    range: "12–60h",
    color: "slate",
    badge: "bg-slate-200 text-slate-800 border-slate-300",
    fill: "bg-slate-500",
  },
  growth: {
    key: "growth",
    label: "Growth Phase",
    short: "Growth",
    range: "60–168h",
    color: "emerald",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    fill: "bg-emerald-500",
  },
  ready: {
    key: "ready",
    label: "Ready for Harvest",
    short: "Ready",
    range: "168h+",
    color: "emerald",
    badge: "bg-emerald-700 text-white border-emerald-800",
    fill: "bg-emerald-700",
  },
};

export const SOAK_HOURS = 12;
export const DARK_END_HOURS = 60;
export const GROWTH_END_HOURS = 168;

export function phaseForHours(hours) {
  if (hours <= SOAK_HOURS) return PHASES.soak;
  if (hours <= DARK_END_HOURS) return PHASES.dark;
  if (hours <= GROWTH_END_HOURS) return PHASES.growth;
  return PHASES.ready;
}

/**
 * Water + light schedule for the current phase.
 * Returns: { lightStatus, lightChangeIn, waterEveryH, nextWaterIn, dailyCycle }
 *   lightStatus    : "off" | "on"
 *   lightChangeIn  : minutes until light toggles next (null if not applicable)
 *   waterEveryH    : 6 (dark) | 4 (growth) | null
 *   nextWaterIn    : minutes until next 1-min watering (null in soak/ready)
 *   dailyCycle     : human description (e.g. "16h ON / 8h OFF")
 */
export function scheduleForHours(hours) {
  const phase = phaseForHours(hours);
  if (phase.key === "soak") {
    return { phase, lightStatus: "off", lightChangeIn: null, waterEveryH: null, nextWaterIn: null, dailyCycle: "No light · no water" };
  }
  if (phase.key === "dark") {
    // first watering at hour 12 (start of dark), then every 6h
    const sinceStart = hours - SOAK_HOURS;
    const nextOffsetH = Math.ceil(sinceStart / 6) * 6;
    let nextH = SOAK_HOURS + nextOffsetH;
    if (nextH <= hours) nextH = SOAK_HOURS + nextOffsetH + 6;
    return {
      phase,
      lightStatus: "off",
      lightChangeIn: null,
      waterEveryH: 6,
      nextWaterIn: Math.max(0, Math.round((nextH - hours) * 60)),
      dailyCycle: "Lights OFF · water 1 min every 6h",
    };
  }
  if (phase.key === "growth") {
    // Light cycle: 16h ON, 8h OFF, starting at hour 60.
    const inGrowth = hours - DARK_END_HOURS;
    const dayPos = ((inGrowth % 24) + 24) % 24; // 0..24
    let lightStatus = "on";
    let lightChangeIn;
    if (dayPos < 16) {
      lightStatus = "on";
      lightChangeIn = Math.round((16 - dayPos) * 60);
    } else {
      lightStatus = "off";
      lightChangeIn = Math.round((24 - dayPos) * 60);
    }
    // Water every 4h starting at hour 60
    const nextOffsetH = Math.ceil(inGrowth / 4) * 4;
    let nextH = DARK_END_HOURS + nextOffsetH;
    if (nextH <= hours) nextH = DARK_END_HOURS + nextOffsetH + 4;
    return {
      phase,
      lightStatus,
      lightChangeIn,
      waterEveryH: 4,
      nextWaterIn: Math.max(0, Math.round((nextH - hours) * 60)),
      dailyCycle: "16h LED ON / 8h OFF · water 1 min every 4h",
    };
  }
  // ready / overdue
  return { phase, lightStatus: "off", lightChangeIn: null, waterEveryH: null, nextWaterIn: null, dailyCycle: "Harvest window — system idle" };
}

export const TEMP_RANGE = { min: 19, max: 24, unit: "°C" };
export const HUMIDITY_RANGE = { min: 60, max: 70, unit: "%" };

// --- Day-based stages (kept for the 7-segment progress bar visuals,
//     relabelled to reflect the hour-based soak→dark→growth model) ----------
export const STAGES = [
  { day: 0, label: "Day 0: Soak (0–12h) → Dark Phase Begins", short: "Soak / Dark", key: "day_0",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    fill: "bg-amber-400" },
  { day: 1, label: "Day 1: Dark Phase (Germination)", short: "Dark Phase", key: "day_1",
    badge: "bg-slate-200 text-slate-800 border-slate-300",
    fill: "bg-slate-500" },
  { day: 2, label: "Day 2: Dark Phase Ends → Growth Begins (60h)", short: "Dark → Growth", key: "day_2",
    badge: "bg-indigo-100 text-indigo-800 border-indigo-300",
    fill: "bg-indigo-400" },
  { day: 3, label: "Day 3: Growth Phase (Lights On)", short: "Growth", key: "day_3",
    badge: "bg-lime-100 text-lime-800 border-lime-300",
    fill: "bg-lime-400" },
  { day: 4, label: "Day 4: Growth Phase (Lights On)", short: "Growth", key: "day_4",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    fill: "bg-emerald-400" },
  { day: 5, label: "Day 5: Chlorophyll Surge & Rapid Growth", short: "Rapid Growth", key: "day_5",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    fill: "bg-emerald-500" },
  { day: 6, label: "Day 6: Final Maturation & Maximum Yield", short: "Maturation", key: "day_6",
    badge: "bg-emerald-200 text-emerald-900 border-emerald-400",
    fill: "bg-emerald-600" },
  { day: 7, label: "Day 7+: Ready for Harvest / Overdue", short: "Ready", key: "day_7",
    badge: "bg-emerald-700 text-white border-emerald-800",
    fill: "bg-emerald-700" },
];

// Zone A → Zone B move happens at this hour (12h soak + 48h dark = 60h)
export const ZONE_MOVE_HOURS = 60;

export function zoneForHours(hours) {
  return hours < ZONE_MOVE_HOURS ? "A" : "B";
}

export const OVERDUE_BADGE = "bg-rose-600 text-white border-rose-700";
export const OVERDUE_FILL = "bg-rose-500";

export function getStageForHours(hours, standardCycleHours = 168) {
  if (hours < 0) return STAGES[0];
  if (hours <= 24) return STAGES[0];
  if (hours <= 48) return STAGES[1];
  if (hours <= 72) return STAGES[2];
  if (hours <= 96) return STAGES[3];
  if (hours <= 120) return STAGES[4];
  if (hours <= 144) return STAGES[5];
  if (hours <= 168) return STAGES[6];
  return STAGES[7];
}

export function isOverdue(hours, standardCycleHours = 168) {
  return hours > standardCycleHours;
}

export function computeOrder(order, nowMs = Date.now()) {
  const start = new Date(order.start_datetime).getTime();
  const elapsedMs = nowMs - start;
  const hoursInProduction = Math.max(0, elapsedMs / (1000 * 60 * 60));
  const daysInProduction = hoursInProduction / 24;
  const cycleDays = order.standard_cycle_days ?? 7;
  const cycleHours = cycleDays * 24;
  const hoursLeft = cycleHours - hoursInProduction;
  const daysLeft = hoursLeft / 24;

  const ratioGrain = order.grain_to_grass_grain_kg;
  const ratioGrass = order.grain_to_grass_grass_kg;
  const trayWeight = order.target_weight_per_tray;

  const traysNeededRaw = order.order_quantity_kg / trayWeight;
  const traysNeededRounded = Math.ceil(traysNeededRaw);
  const grainNeeded = (order.order_quantity_kg / ratioGrass) * ratioGrain;

  const stage = getStageForHours(hoursInProduction, cycleHours);
  const phase = phaseForHours(hoursInProduction);
  const schedule = scheduleForHours(hoursInProduction);
  const overdue = isOverdue(hoursInProduction, cycleHours);
  // Filled day count for progress bar: 0..7
  const filledDays = Math.min(7, Math.max(0, Math.floor(hoursInProduction / 24) + (hoursInProduction > 0 ? 1 : 0)));
  const segmentsFilled = hoursInProduction <= 0 ? 0 : Math.min(7, Math.ceil(hoursInProduction / 24));

  return {
    hoursInProduction,
    daysInProduction,
    hoursLeft,
    daysLeft,
    traysNeededRaw,
    traysNeededRounded,
    grainNeeded,
    stage,
    phase,
    schedule,
    overdue,
    segmentsFilled,
    cycleHours,
  };
}

export function formatHoursAsDH(hours) {
  if (hours < 0) hours = 0;
  const d = Math.floor(hours / 24);
  const h = Math.floor(hours - d * 24);
  const m = Math.floor((hours - Math.floor(hours)) * 60);
  return `${d}d ${h}h ${m}m`;
}

export function formatMinutes(mins) {
  if (mins === null || mins === undefined || Number.isNaN(mins)) return "—";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function formatNum(n, decimals = 2) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}
